import {
  alpha,
  Box,
  type BoxProps,
  Card,
  CardContent,
  CardHeader,
  type CardProps,
  Collapse,
  Divider,
  Grid,
  Link as MLink,
  Paper,
  Skeleton,
  type SkeletonProps,
  Stack,
  type Theme,
  Typography,
  type TypographyProps,
  useTheme,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import {
  type ComponentId,
  objectEntries,
  type PathKeys,
  prettifySnakeCase,
} from '@poweralpha/common';
import type { DeepKeys, StandardSchemaV1Issue } from '@tanstack/react-form';
import { AnimatePresence, motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { isEmpty, kebabCase, merge, uniqBy, uniqueId } from 'lodash';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFirstError, trimString } from '../utils';
import { ExpandIconButton } from './buttons';
import usePANavigate from './navigate/usePANavigate';
import { collapsedFieldsetAtom } from './store';
import { WrapText } from './text';
import { useDebounce } from './useDebounce';

interface InputCardProps<T> extends CardProps {
  label: string | React.ReactNode;
  children: ReactElement | ReactElement[];
  action1?: ReactElement;
  errors?: Partial<Record<string, unknown>>;
  errorMap?: Partial<Record<PathKeys<T>, StandardSchemaV1Issue>>;
  iconSx?: SxProps<Theme>;
  collapseIconLeftOfTitle?: boolean;
  collapseable?: boolean;
  defaultCollapsed?: boolean;
  disableCollapse?: boolean;
  transparentBackground?: boolean;
}

export function InputCard<T>({
  label,
  children,
  action1,
  errors,
  errorMap,
  iconSx,
  collapseable = true,
  defaultCollapsed = false,
  disableCollapse = false,
  transparentBackground = false,
  variant = 'elevation',
  collapseIconLeftOfTitle,
  ...rest
}: InputCardProps<T>) {
  const theme = useTheme();

  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const errorString = useMemo(() => {
    if (!isEmpty(errors)) {
      return getFirstError(errors);
    }
    if (isEmpty(errorMap)) return '';
    const [key, error] = objectEntries(errorMap)[0] as [PathKeys<T>, StandardSchemaV1Issue];
    return `${prettifySnakeCase(key.split('.').at(-1))}: ${error.message}`;
  }, [errors, errorMap]);

  const handleClick = useCallback(() => setCollapsed((prevState) => !prevState), []);

  const collapseIcon = (
    <Grid hidden={collapseable ? disableCollapse : true}>
      <ExpandIconButton
        collapsed={collapsed}
        onClick={action1 ? handleClick : undefined}
        disabled={!collapseable || disableCollapse}
        size="small"
        reverse={!collapseIconLeftOfTitle}
        data-testid={`${label}-collapse`}
        sx={iconSx}
      />
    </Grid>
  );

  return (
    <Card variant={variant} {...rest}>
      <CardHeader
        title={
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            {collapseIconLeftOfTitle && collapseIcon}
            <Box
              sx={{
                color: errorString !== '' ? 'error.main' : 'primary',
              }}
            >
              {label}
            </Box>
          </Stack>
        }
        subheader={
          <Typography
            sx={{
              fontSize: 12,
              color: 'error.main',
            }}
          >
            {errorString}
          </Typography>
        }
        action={
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            {action1 ? <Grid>{action1}</Grid> : null}
            {!collapseIconLeftOfTitle && collapseIcon}
          </Stack>
        }
        data-testid={`${label}-inputs`}
        onClick={collapseable && !disableCollapse && !action1 ? handleClick : undefined}
        sx={merge({ p: 1.5, pr: 2 }, collapseable && !disableCollapse ? { cursor: 'pointer' } : {})}
      />
      <Divider />
      <Collapse in={!collapsed} mountOnEnter unmountOnExit>
        <CardContent
          sx={
            transparentBackground
              ? { bgcolor: theme.palette.background.default, height: '100%' }
              : { height: '100%' }
          }
        >
          {children}
        </CardContent>
      </Collapse>
    </Card>
  );
}

interface BubbleCardProps extends CardProps {
  children: ReactElement | ReactElement[];
  label?: string;
  titleSize?: TypographyProps['variant'];
  action?: React.ReactNode;
}

export const BubbleCard: React.FC<BubbleCardProps> = ({
  children,
  label,
  action,
  titleSize = 'h6',
  ...rest
}: BubbleCardProps) => (
  <Card {...rest} variant="elevation" data-testid={kebabCase(label)}>
    {label && (
      <CardHeader title={<Typography variant={titleSize}>{label}</Typography>} action={action} />
    )}
    <CardContent>{children}</CardContent>
  </Card>
);

export function PageErrorCard({ error = '403 Forbidden' }: Readonly<{ error?: string }>) {
  const { navigate } = usePANavigate();

  return (
    <Paper variant="outlined" sx={{ height: 50 }}>
      <Stack
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography>{error}</Typography>
        <MLink variant="body2" onClick={() => navigate(-1)}>
          Go Back
        </MLink>
      </Stack>
    </Paper>
  );
}

const fieldsetBoxSx = {
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  paddingTop: 0,
  '& legend': {
    fontSize: 12,
    color: 'text.secondary',
  },
};

interface FieldsetProps extends BoxProps {
  children: React.ReactNode | React.ReactNode[];
  legend: React.ReactNode;
  legendProps?: BoxProps;
  bgcolor?: string;
  compact?: boolean;
}

export function Fieldset({
  children,
  legend,
  sx,
  legendProps,
  bgcolor,
  compact = false,
  ...rest
}: Readonly<FieldsetProps>) {
  const { color: legendColor, sx: legendSx, ...restLegendProps } = legendProps ?? {};
  let resolvedLegendSx = legendSx;
  if (legendColor) {
    resolvedLegendSx = { color: legendColor };
    if (legendSx && typeof legendSx === 'object' && !Array.isArray(legendSx)) {
      resolvedLegendSx = { ...legendSx, color: legendColor };
    }
  }

  return (
    <Box
      component="fieldset"
      sx={{
        ...fieldsetBoxSx,
        ...(compact ? { px: 1, py: 0.75 } : {}),
        ...(bgcolor ? { bgcolor } : {}),
        pt: 1,
        ...sx,
      }}
      {...rest}
    >
      <legend>
        <Box {...restLegendProps} sx={resolvedLegendSx}>
          {legend}
        </Box>
      </legend>
      {children}
    </Box>
  );
}

interface CollapseableFieldsetProps extends FieldsetProps, Partial<ComponentId> {
  topBar?: React.ReactNode;
  initiallyCollapsed?: boolean;
}

export function CollapsibleFieldset({
  children,
  legend,
  initiallyCollapsed,
  topBar,
  id,
  ...rest
}: Readonly<CollapseableFieldsetProps>) {
  const identifier = useMemo(() => id ?? uniqueId('collapsible-fieldset-'), [id]);
  const [collapsedFieldsets, setCollapsedFieldsets] = useAtom(collapsedFieldsetAtom);
  const isCollapsed = collapsedFieldsets[identifier] ?? true;
  const initiallyCollapseFieldSets = useRef(collapsedFieldsets);

  const handleClick = useCallback(
    () =>
      setCollapsedFieldsets((prev) => ({
        ...prev,
        [identifier]: prev[identifier] !== undefined ? !prev[identifier] : false,
      })),
    [setCollapsedFieldsets, identifier],
  );

  useEffect(() => {
    const initial = initiallyCollapseFieldSets.current;

    if (initiallyCollapsed !== undefined && initiallyCollapsed !== null) {
      setCollapsedFieldsets((prev) => ({ ...prev, [identifier]: initiallyCollapsed }));
    }

    return () => setCollapsedFieldsets(initial);
  }, [identifier, setCollapsedFieldsets, initiallyCollapsed]);

  return (
    <Fieldset
      legend={
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <ExpandIconButton
            collapsed={isCollapsed}
            onClick={handleClick}
            sx={{ height: 15, width: 15 }}
            data-testid={`${id}-collapse`}
          />
          {legend}
        </Stack>
      }
      bgcolor="background.paper"
      {...rest}
    >
      {topBar}
      {/* unmountOnExit: collapsed fieldsets cost nothing (no layout/reflow of the
          heavy form subtree) and the page doesn't hold every bar's DOM at once */}
      <Collapse in={!isCollapsed} unmountOnExit mountOnEnter>
        {children}
      </Collapse>
    </Fieldset>
  );
}

interface CollapseableSectionProps extends TypographyProps {
  label: string;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  disableCollapse?: boolean;
  hideDivider?: boolean;
}

export const CollapseableSection = ({
  label,
  children,
  defaultCollapsed,
  disableCollapse,
  hideDivider,
  ...rest
}: CollapseableSectionProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);

  const handleCollapseClick = useCallback(() => setCollapsed((prev) => !prev), []);

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <ExpandIconButton
          collapsed={collapsed}
          onClick={handleCollapseClick}
          size="small"
          disabled={disableCollapse}
          data-testid={`${label}-collapse`}
        />
        <Stack
          sx={{
            width: '100%',
          }}
        >
          <Typography
            variant="subtitle1"
            onClick={handleCollapseClick}
            sx={{ cursor: 'pointer' }}
            {...rest}
          >
            {label}
          </Typography>
          {!hideDivider && <Divider />}
        </Stack>
      </Stack>
      <AnimatePresence initial={false}>
        {collapsed ? null : (
          <motion.div
            key="collapseable-section"
            style={{ overflow: 'hidden' }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
};

interface MeasuredCollapseProps {
  in: boolean;
  children: React.ReactNode;
  durationMs?: number;
}

// this component is equivalent to MUI Collapse, however solves some performance issues
// causing FPS drops when animating
export const MeasuredCollapse = ({
  in: inProp,
  children,
  durationMs = 200,
}: MeasuredCollapseProps) => {
  return (
    <AnimatePresence initial={false}>
      {inProp && (
        <motion.div
          key="measured-collapse"
          style={{ overflow: 'hidden' }}
          layout
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            duration: durationMs / 1000,
            ease: [0.4, 0, 0.2, 1],
            layout: { duration: durationMs / 1000, ease: [0.4, 0, 0.2, 1] },
          }}
        >
          <motion.div layout>{children}</motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface LoadingBoundaryProps extends SkeletonProps {
  children: React.ReactNode;
  customSkeleton?: React.ReactNode;
  isLoading: boolean | undefined;
}

export const LoadingBoundary = ({
  isLoading,
  children,
  customSkeleton,
  ...props
}: Readonly<LoadingBoundaryProps>) => {
  return isLoading
    ? (customSkeleton ?? (
        <Skeleton {...props}>{props.height && props.width ? undefined : children}</Skeleton>
      ))
    : children;
};

const ErrorEntry = ({ name, issues }: { name: string; issues: Array<StandardSchemaV1Issue> }) => {
  const firstError = issues[0];

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
        {name}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          ml: 2,
          lineHeight: 1.1,
        }}
      >
        {trimString(firstError.message, 100)}
      </Typography>
    </Box>
  );
};

export interface ErrorListingProps<T> {
  errors: Partial<Record<DeepKeys<T>, Array<StandardSchemaV1Issue>>>;
  replacements?: Partial<Record<DeepKeys<T>, string>>;
  filterKeys?: Array<DeepKeys<T>>;
}

export function ErrorListing<T>({
  errors,
  replacements,
  filterKeys,
}: Readonly<ErrorListingProps<T>>) {
  const theme = useTheme();

  const debounced = useDebounce(errors, 1e3);

  const errorEntries = useMemo(() => {
    const entries = objectEntries(debounced)
      .filter(([path]) => (filterKeys ? !filterKeys.includes(path as DeepKeys<T>) : true))
      .map(
        ([path, issues]) =>
          [
            replacements?.[(path as string).replace(/\[\d+\]/g, '[0]') as DeepKeys<T>] ??
              prettifySnakeCase((path as string).split('.').at(-1)),
            issues as Array<StandardSchemaV1Issue>,
          ] satisfies [string, Array<StandardSchemaV1Issue>],
      );

    return uniqBy(entries, ([name]) => name);
  }, [debounced, replacements, filterKeys]);

  return (
    <MeasuredCollapse in={Boolean(errorEntries.length)} durationMs={200}>
      <Fieldset
        legend={`Errors (${errorEntries.length})`}
        legendProps={{ color: 'error.main' }}
        sx={{
          borderColor: theme.palette.error.main,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.error.main, 0.05),
        }}
        data-testid="error-listing"
      >
        {errorEntries.slice(0, 5).map(([name, issues]) => (
          <ErrorEntry key={name} name={name} issues={issues} />
        ))}
      </Fieldset>
    </MeasuredCollapse>
  );
}

const WarningEntry = ({ name, issue }: { name: string; issue: string }) => {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {name}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          ml: 2,
        }}
      >
        <WrapText>{issue}</WrapText>
      </Typography>
    </Box>
  );
};

interface WarningListingProps {
  warnings: Record<string, string>;
}

export function WarningListing({ warnings }: Readonly<WarningListingProps>) {
  const theme = useTheme();

  const debounced = useDebounce(warnings, 1e3);
  const warningEntries = Object.entries(debounced);

  return (
    <MeasuredCollapse in={Boolean(warningEntries.length)}>
      <Fieldset
        legend={`Warnings (${warningEntries.length})`}
        legendProps={{ color: 'warning.main' }}
        sx={{
          borderColor: theme.palette.warning.main,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.05),
        }}
        data-testid="warning-listing"
      >
        {warningEntries.slice(0, 5).map(([name, issue]) => (
          <WarningEntry key={name} name={name} issue={issue} />
        ))}
      </Fieldset>
    </MeasuredCollapse>
  );
}

interface MiniCardProps extends CardProps {
  children: React.ReactNode | Array<React.ReactNode>;
  label: React.ReactNode;
}

export function MiniCard({ children, label, ...props }: Readonly<MiniCardProps>) {
  return (
    <Card variant="outlined" {...props}>
      <Typography
        variant="h6"
        sx={{
          p: 0.5,
          pl: 2,
        }}
      >
        {label}
      </Typography>
      {children}
    </Card>
  );
}
